import { emitMobileTelemetryEvent } from '../../mobile/navigation/telemetry';
import { CONFERENCE_JOINED, CONFERENCE_LEFT, CONFERENCE_WILL_LEAVE } from '../conference/actionTypes';
import { VIDEO_TYPE } from '../media/constants';
import MiddlewareRegistry from '../redux/MiddlewareRegistry';
import { createCameraReacquirePolicy } from './cameraReacquirePolicy';
import { getLocalVideoTrack } from './functions.any';
/*
 * Web: the room's camera is re-acquired once when its track ends (camera
 * pulled, browser stopped it) or its source keeps it muted (the browser
 * suspended the camera while the page was in the background) while the page
 * is visible. Policy: cameraReacquirePolicy.ts; the replacement itself is
 * conference.js reacquireLocalCamera.
 */
const CAMERA_PROBE_MS = 1500;
const WEB_SILENT_GRACE_MS = 3000;
let activeConference;
let policy;
let timer;
let onVisibility;
/**
 * The room's camera as the policy sees it.
 *
 * @param {Function} getState - Redux getState.
 * @returns {ICameraProbe}
 */
export function probeWebCamera(getState) {
    const track = getLocalVideoTrack(getState()['features/base/tracks']);
    const jitsiTrack = track?.jitsiTrack;
    const media = typeof jitsiTrack?.getTrack === 'function' ? jitsiTrack.getTrack() : undefined;
    return {
        at: Date.now(),
        ended: media?.readyState === 'ended',
        isCamera: Boolean(jitsiTrack) && jitsiTrack.getVideoType?.() !== VIDEO_TYPE.DESKTOP,
        silent: media?.muted === true,
        track: jitsiTrack,
        userMuted: Boolean(track?.muted),
        visible: typeof document === 'undefined' || document.visibilityState === 'visible'
    };
}
/**
 * Samples the camera and, when the policy says so, re-acquires it once.
 *
 * @param {IStore} store - The redux store.
 * @returns {void}
 */
function check({ getState }) {
    const probe = probeWebCamera(getState);
    const decision = policy?.observe(probe);
    const conference = typeof APP === 'undefined' ? undefined : APP.conference;
    if (!decision?.reacquire || typeof conference?.reacquireLocalCamera !== 'function') {
        return;
    }
    const roomName = getState()['features/base/conference']?.room;
    Promise.resolve(conference.reacquireLocalCamera(probe.track))
        .catch((error) => {
        return {
            errorName: error?.name,
            outcome: 'failed'
        };
    })
        .then((result) => {
        emitMobileTelemetryEvent('local_camera_reacquire', {
            error_name: result?.errorName,
            outcome: result?.outcome,
            reason: decision.reason
        }, {
            roomName,
            state: result?.outcome
        });
    });
}
/**
 * Stops sampling: the call left.
 *
 * @returns {void}
 */
function stop() {
    if (timer) {
        clearInterval(timer);
    }
    if (onVisibility && typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVisibility);
    }
    activeConference = undefined;
    onVisibility = undefined;
    policy = undefined;
    timer = undefined;
}
MiddlewareRegistry.register(store => next => action => {
    const result = next(action);
    switch (action.type) {
        case CONFERENCE_JOINED:
            stop();
            activeConference = action.conference;
            policy = createCameraReacquirePolicy({ silentGraceMs: WEB_SILENT_GRACE_MS });
            timer = setInterval(() => check(store), CAMERA_PROBE_MS);
            onVisibility = () => check(store);
            if (typeof document !== 'undefined') {
                document.addEventListener('visibilitychange', onVisibility);
            }
            break;
        case CONFERENCE_LEFT:
        case CONFERENCE_WILL_LEAVE:
            if (action.conference === activeConference) {
                stop();
            }
            break;
    }
    return result;
});
